// StagingTab — Feb 22, 2026 (one-shot full build).
//
// In-silo "Staging" tab (positioned between Library and Workbench).
// Three sections stacked top-to-bottom:
//   1. Header + (i) tooltip + PWA install nudge
//   2. Impromptu staging — voice → Whisper → text → push-to-silo
//   3. Custom question bank + scheduled push alerts
//
// Mobile-first. All toasts deduped with stable IDs.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Lightning, Microphone, Stop, PaperPlaneRight, Bell, Plus, Trash,
  X, ArrowsClockwise, CheckCircle, Pause, Play, Spinner, DownloadSimple,
  House, Sparkle, PencilSimple, Eye, CaretLeft, ArrowSquareOut, Info,
  Shuffle, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DocSaveDialog from "@/components/DocSaveDialog";
import SplitAndPushModal, { needsSplit } from "@/components/SplitAndPushModal";
import CalendarSection, { EventEditorModal } from "@/components/CalendarSection";
import SmartCaptureModal from "@/components/SmartCaptureModal";
import SmartEditPreviewModal from "@/components/SmartEditPreviewModal";
import SmartEditUndoCenter from "@/components/SmartEditUndoCenter";
import PingTypesExplainer from "@/components/PingTypesExplainer";
import InfoTip from "@/components/InfoTip";
import FamilyAsk from "@/components/FamilyAsk";
import { useEntitlements } from "@/lib/entitlements";

const DOW_LABELS = ["S","M","T","W","T","F","S"];
const HOURS_12 = Array.from({ length: 12 }, (_, i) => i === 0 ? 12 : i);
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

export default function StagingTab({ siloId, siloName, siloSlug, allSilos = [], inheritedSkus = [] }) {
  // ============ PWA install detection / nudge ============
  const [pwaInstalled, setPwaInstalled] = useState(false);
  const [ackInstall, setAckInstall] = useState(() => localStorage.getItem("staging_pwa_ack") === "1");
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(display-mode: standalone)");
    const update = () => setPwaInstalled(m.matches || window.navigator.standalone === true);
    update();
    m.addEventListener?.("change", update);
    return () => m.removeEventListener?.("change", update);
  }, []);
  // Welcome-to-Staging CTA. Feb 24, 2026 (Randy pt89c-xxi) — rewritten
  // from "auto-open once, ever" → "auto-open on roughly 1-in-5 opens of
  // this tab, and only if the user hasn't hit 'Don't show again'." Both
  // counter and kill-switch are localStorage so they survive browser
  // resets. The Eye button next to the title still opens it on demand.
  const [helpView, setHelpView] = useState("welcome");
  // Feb 25, 2026 (Randy pt89c-xxvi) — TIME-BASED gating (industry standard),
  // replacing the old per-mount counter. Counting component mounts was
  // fragile: React.StrictMode double-invokes effects AND this tab can remount
  // several times per real visit, so the old `n % 5 === 0` raced past
  // multiples of 5 almost every visit → the modal nagged constantly and felt
  // like it ignored "Don't show again". A timestamp is immune to mount count:
  // show at most once per 72h, and NEVER once the user ticks "Don't show this
  // again" (checked first, so the kill-switch is absolute).
  useEffect(() => {
    try {
      if (localStorage.getItem("staging_welcome_never_ask") === "1") return;
      const last = parseInt(localStorage.getItem("staging_welcome_last_shown") || "0", 10);
      const COOLDOWN_MS = 72 * 60 * 60 * 1000; // 72 hours
      if (last && Number.isFinite(last) && (Date.now() - last) < COOLDOWN_MS) return;
      localStorage.setItem("staging_welcome_last_shown", String(Date.now()));
      setHelpView("welcome");
      setShowInstallHelp(true);
    } catch { /* ignore */ }
  }, []);

  // ============ Impromptu staging text ============
  const [stagingText, setStagingText] = useState("");
  const stagingRef = useRef(null);
  // Feb 22, 2026 (Randy pt89c-iii) — persist the staging pad text so a
  // browser refresh / accidental logout / tab switch doesn't lose it.
  // Keyed by silo so different silos don't cross-contaminate.
  const STAGING_LS_KEY = `mysilo:staging-pad-text:${siloId}`;
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(STAGING_LS_KEY);
      if (saved) setStagingText(saved);
    } catch { /* localStorage unavailable */ }
  }, [siloId]);
  useEffect(() => {
    try {
      if (stagingText) window.localStorage.setItem(STAGING_LS_KEY, stagingText);
      else window.localStorage.removeItem(STAGING_LS_KEY);
    } catch { /* ignore */ }
  }, [STAGING_LS_KEY, stagingText]);
  // Feb 2026 (Randy) — the Smart Create All Chores AI plan (and any future
  // Smart-fill feature) delivers its output into the pad via a custom
  // window event so the mega-button can live inside HouseholdPanel without
  // prop drilling. Payload: { detail: { text, source } }.
  useEffect(() => {
    const onFill = (ev) => {
      const txt = String(ev?.detail?.text || "").trim();
      if (!txt) return;
      setStagingText((cur) => (cur.trim() ? `${cur.trimEnd()}\n\n${txt}` : txt));
      // Scroll the pad into view + focus, so the user can immediately edit
      // or hit Smart create.
      setTimeout(() => {
        try {
          stagingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          stagingRef.current?.focus?.();
        } catch { /* noop */ }
      }, 60);
    };
    window.addEventListener("mysilo:capture-pad-fill", onFill);
    return () => window.removeEventListener("mysilo:capture-pad-fill", onFill);
  }, []);
  // Feb 2026 (Randy) — Push-to-silo default tier is now silo-kind-aware.
  // Personal / Organizer silos → Tier 1 (their data footprint is light,
  // and the always-active anchor benefits from having capture-pad memory
  // sit at the top). Public / YouTube-Twin silos → Tier 2 (traditional
  // creator libraries can grow huge; Tier 1 would balloon prompt costs).
  const currentSiloKind = allSilos.find((s) => s.id === siloId)?.silo_kind || "personal";
  const defaultPushTier = (currentSiloKind === "personal" || currentSiloKind === "organizer") ? 1 : 2;
  // Jul 2026 — the "Push to silo" flow now routes through the shared
  // DocSaveDialog (title field + duplicate-name guard + Hide + tier +
  // destination silo), identical to the Library / Workbench / Refinery saves.
  const [commitState, setCommitState] = useState({ open: false, text: "", defaultTitle: "" });
  const [splitState, setSplitState] = useState({ open: false, content: "", siloId: "", tier: defaultPushTier, title: "" });

  // ============ Voice recording ============
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recElapsed, setRecElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  // Jun 2026 — live "words as you talk" preview. Uses the browser's own free
  // speech engine (Chrome/Safari) purely for on-screen feedback while you
  // dictate. The SAVED text still comes from Whisper (your OpenAI key) below,
  // so transcription quality is unchanged. Firefox has no engine → no preview,
  // but the final Whisper transcript still works.
  const [liveTranscript, setLiveTranscript] = useState("");
  const recognitionRef = useRef(null);
  const startLivePreview = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = navigator.language || "en-US";
      let finalSoFar = "";
      rec.onresult = (ev) => {
        let interim = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const txt = ev.results[i][0].transcript;
          if (ev.results[i].isFinal) finalSoFar += txt + " ";
          else interim += txt;
        }
        setLiveTranscript((finalSoFar + interim).trim());
      };
      rec.onerror = () => {};
      recognitionRef.current = rec;
      rec.start();
    } catch { /* preview is best-effort */ }
  };
  const stopLivePreview = () => {
    try { recognitionRef.current?.stop(); } catch { /* */ }
    recognitionRef.current = null;
  };

  // Jun 24, 2026 (Randy) — reuse ONE live mic stream so iOS prompts for
  // microphone permission at most ONCE per visit to this screen, instead of
  // on every recording (it re-prompted before because each record stopped the
  // tracks). The stream is released on unmount (see cleanup effect below), so
  // the mic only stays warm while you're on the Staging capture screen.
  const ensureMicStream = async () => {
    if (streamRef.current && streamRef.current.getTracks().some((t) => t.readyState === "live")) {
      return streamRef.current;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    return stream;
  };

  const startRecording = async () => {
    try {
      const stream = await ensureMicStream();
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        // Keep the mic stream alive for the next recording — no re-prompt.
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribeAndAppend(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setRecElapsed(0);
      setLiveTranscript("");
      startLivePreview();
      timerRef.current = setInterval(() => setRecElapsed((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Couldn't access the microphone. Check your browser's permissions.", { id: "mic-denied" });
    }
  };
  const stopRecording = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      mediaRef.current = null;
      setRecording(false);
      stopLivePreview();
      setLiveTranscript("");
      clearInterval(timerRef.current);
    }
  };

  // Release the mic + timers when leaving the Staging capture screen.
  useEffect(() => {
    return () => {
      try { mediaRef.current?.stop(); } catch { /* */ }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      clearInterval(timerRef.current);
      try { recognitionRef.current?.stop(); } catch { /* */ }
    };
  }, []);
  const transcribeAndAppend = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "rec.webm");
      // Jun 2026 — dictation now runs on the silo owner's OpenAI key (owner-pays),
      // so the backend needs to know which silo to bill + gate.
      fd.append("silo_id", siloId);
      const r = await api.post("/staging/voice-transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const t = (r.data?.text || "").trim();
      if (t) {
        // Append (with a space separator) so multiple recordings stitch together.
        setStagingText((cur) => (cur ? cur.trim() + "\n\n" + t : t));
      } else {
        toast("Got an empty transcript. Try again with a louder recording.", { id: "empty-tx" });
      }
    } catch (e) {
      // 402 LLM_GUARD (no OpenAI key + empty OpenAI voucher) is handled by the
      // global top-up/connect-key modal — don't double up with a toast.
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") {
        return;
      }
      toast.error(e.response?.data?.detail || "Transcription failed.", { id: "tx-fail" });
    } finally { setTranscribing(false); }
  };

  // ============ Push staging to silo ============
  // Opens the shared DocSaveDialog (commit mode). The dialog owns the
  // destination silo, tier, title, Hide toggle, duplicate-name guard and the
  // oversize → Split & Push handoff — so Staging saves behave exactly like
  // every other storage entry point.
  const pushToSilo = () => {
    const content = (stagingText || "").trim();
    if (!content) {
      toast.error("Nothing to push yet — write or dictate something first.", { id: "empty-push" });
      return;
    }
    const title = content.split("\n")[0].slice(0, 80) || "Staging note";
    setCommitState({ open: true, text: content, defaultTitle: title });
  };

  // ============ Smart Capture (Jul 2026, AI Calendar) ============
  // "Smart create" sends the pad text to the owner's LLM key, which classifies
  // it into events / tasks / routines / pings / bank questions. A review modal
  // gates everything — nothing is created without an explicit accept.
  const { owns } = useEntitlements();
  const [smartBusy, setSmartBusy] = useState(false);
  const [smartItems, setSmartItems] = useState(null);
  const [smartProgress, setSmartProgress] = useState(null);
  const [pingEditor, setPingEditor] = useState(null); // defaults for "save as ping"
  const [showPingInfo, setShowPingInfo] = useState(false);
  // Feb 24, 2026 (Randy pt89c-xxii Phase 1) — Smart Edit auto-routing (also
  // wired in FamilyCapturePad). Same ONE button UX ("Smart create"). The
  // client-side edit-verb regex flips CREATE→EDIT; backend re-validates
  // and gracefully returns to CREATE if no matching events are found.
  const [smartEditJobId, setSmartEditJobId] = useState(null);
  // Feb 26, 2026 (Randy pt-smart-create-hijack-fix). Old regex fired
  // Smart Edit whenever ANY word like "push" or "split" appeared
  // anywhere in the pad — so bulk workout schedules ("5-day push/pull
  // split") got misrouted to /smart-edit and errored with "No matching
  // events found" on fresh silos. Tightened to only match when the
  // pad text reads like an IMPERATIVE edit COMMAND:
  //   • starts with an edit verb (optional polite prefix),
  //   • AND is short (≤ 240 chars — bulk data is never an edit).
  // Anything else stays a create, which is what "Smart create" promises.
  const STAGING_EDIT_CMD_RE = /^\s*(?:please\s+)?(?:can\s+you\s+)?(?:change|move|swap|delete|remove|reschedule|replace|reassign|combine|merge|split|shift|postpone|push\s+back|bump|cancel|mark(?:ed)?)\b/i;
  const isStagingEditIntent = (t) => t.length <= 240 && STAGING_EDIT_CMD_RE.test(t);
  const smartCreate = async () => {
    const text = (stagingText || "").trim();
    if (!text) {
      toast.error("Nothing to parse yet — write or dictate something first.", { id: "empty-smart" });
      return;
    }
    if (isStagingEditIntent(text)) {
      setSmartBusy(true);
      try {
        const r = await api.post(`/calendar/${siloId}/smart-edit`, {
          text, tz_offset_minutes: new Date().getTimezoneOffset(),
        });
        const jobId = r.data?.job_id;
        if (!jobId) { toast.error("Couldn't start Smart Edit — try again."); return; }
        setSmartEditJobId(jobId);
      } catch (e) {
        if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
        toast.error(e.response?.data?.detail || "Smart Edit failed.", { id: "staging-smart-edit-fail" });
      } finally { setSmartBusy(false); }
      return;
    }
    setSmartBusy(true); setSmartProgress(null);
    try {
      const r = await api.post(`/calendar/${siloId}/smart-capture/bulk`, {
        text, tz_offset_minutes: new Date().getTimezoneOffset(),
      });
      const jobId = r.data?.job_id;
      const totalChunks = r.data?.chunks || 1;
      if (!jobId) { toast.error("Smart create didn't start — try again."); return; }
      if (totalChunks > 1) {
        toast.success(`Parsing in ${totalChunks} chunks — this may take a minute.`, { id: "bulk-start" });
      }
      setSmartProgress({ done: 0, total: totalChunks });
      let items = []; let done = false; let errMsg = null; let chunkStats = [];
      for (let i = 0; i < 90; i += 1) {
        await new Promise((res) => setTimeout(res, 2000));
        try {
          const pr = await api.get(`/calendar/smart-capture/bulk/job/${jobId}`);
          const st = pr.data?.status;
          const prog = pr.data?.progress;
          if (prog) setSmartProgress(prog);
          if (st === "done") { items = pr.data?.items || []; chunkStats = pr.data?.chunk_stats || []; done = true; break; }
          if (st === "error") { errMsg = pr.data?.error || "Smart create failed."; done = true; break; }
        } catch { /* transient */ }
      }
      if (!done) { toast.error("Smart create is taking longer than expected — try again.", { id: "bulk-slow" }); return; }
      if (errMsg) { toast.error(errMsg, { id: "bulk-err" }); return; }
      const failed = chunkStats.filter((s) => s?.status === "failed");
      if (failed.length) {
        toast.warning(`${failed.length} of ${chunkStats.length} chunk${failed.length === 1 ? "" : "s"} couldn't parse — the rest committed. You can rerun to try again.`, { id: "bulk-partial", duration: 8000 });
      }
      setSmartItems(items);
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
      toast.error(e.response?.data?.detail || "Smart create failed.", { id: "smart-fail" });
    } finally { setSmartBusy(false); setSmartProgress(null); }
  };

  // ============ Question bank ============
  const [bank, setBank] = useState([]);
  const [newBankQ, setNewBankQ] = useState("");
  const loadBank = async () => {
    try {
      const r = await api.get(`/staging/banks/${siloId}`);
      setBank(r.data?.questions || []);
    } catch { /* empty */ }
  };
  useEffect(() => { loadBank(); /* eslint-disable-next-line */ }, [siloId]);

  const addBankQ = async () => {
    const q = (newBankQ || "").trim();
    if (!q) return;
    try {
      await api.post(`/staging/banks/${siloId}`, { question: q });
      setNewBankQ("");
      loadBank();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save question.", { id: "bank-add-fail" });
    }
  };
  const delBankQ = async (id) => {
    if (!window.confirm("Remove this question from the bank?")) return;
    try {
      await api.delete(`/staging/banks/${siloId}/${id}`);
      loadBank();
    } catch { toast.error("Delete failed."); }
  };
  // May 25, 2026 — inline edit for bank questions.
  const [editingBankId, setEditingBankId] = useState(null);
  const [editingBankText, setEditingBankText] = useState("");
  const startEditBankQ = (b) => {
    setEditingBankId(b.id);
    setEditingBankText(b.question);
  };
  const saveEditBankQ = async () => {
    const text = (editingBankText || "").trim();
    if (!text) { toast.error("Question can't be empty."); return; }
    try {
      await api.patch(`/staging/banks/${siloId}/${editingBankId}`, { question: text });
      setEditingBankId(null);
      setEditingBankText("");
      loadBank();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save edit.");
    }
  };

  // ============ Push subscription / permission ============
  const [pushPerm, setPushPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [pushBusy, setPushBusy] = useState(false);

  const subscribePush = async () => {
    setPushBusy(true);
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.error("This browser doesn't support push notifications.", { id: "push-unsup" });
        return;
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      setPushPerm(perm);
      if (perm !== "granted") {
        toast.error("Notifications blocked. Enable them in browser settings.", { id: "push-denied" });
        return;
      }
      const { data: keyResp } = await api.get("/staging/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyResp.public_key),
      });
      await api.post("/staging/push/subscribe", {
        endpoint: sub.endpoint,
        keys: { p256dh: arrayBufferToBase64(sub.getKey("p256dh")), auth: arrayBufferToBase64(sub.getKey("auth")) },
        user_agent: navigator.userAgent,
        // May 25, 2026 — scope this subscription to the silo it was
        // created in. Without it, every PWA install of this user
        // receives pings for every silo's alerts.
        silo_slug: siloSlug || null,
      });
      toast.success("Push notifications enabled on this device.");
    } catch (e) {
      toast.error("Couldn't enable push: " + (e.message || e), { id: "push-setup-fail" });
    } finally { setPushBusy(false); }
  };

  const testPush = async () => {
    try {
      const r = await api.post("/staging/push/test");
      if (r.data?.sent > 0) toast.success(`Sent ${r.data.sent} test ping. Check your lockscreen!`);
      else toast.error("No active push subscriptions found.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed.");
    }
  };

  // ============ Alerts CRUD ============
  const [alerts, setAlerts] = useState([]);
  const [showNewAlert, setShowNewAlert] = useState(false);
  // May 25, 2026 — `editingAlert` state holds the alert object currently
  // being edited. NewAlertModal pulls double-duty (create + edit) when
  // this is set.
  const [editingAlert, setEditingAlert] = useState(null);
  const loadAlerts = async () => {
    try {
      const r = await api.get("/staging/alerts");
      setAlerts((r.data?.alerts || []).filter((a) => a.silo_id === siloId));
    } catch { /* empty */ }
  };
  useEffect(() => { loadAlerts(); /* eslint-disable-next-line */ }, [siloId]);

  // ============ Collaborators (for the alert "Send to" dropdown) ============
  // Feb 23, 2026 — a silo can have N collaborators. When the user creates
  // an alert, we let them pick who the ping fires on. Also surfaces a
  // small "(N) collaborators" pill at the top of the tab.
  const [collabData, setCollabData] = useState(null);
  const loadCollab = async () => {
    try {
      const r = await api.get(`/silos/${siloId}/collaborators`);
      setCollabData(r.data);
    } catch { setCollabData(null); }
  };
  useEffect(() => { loadCollab(); /* eslint-disable-next-line */ }, [siloId]);
  const teamMembers = [];
  if (collabData?.owner) teamMembers.push({ ...collabData.owner, role: "owner" });
  for (const c of (collabData?.collaborators || [])) {
    teamMembers.push({ ...c, role: "collaborator" });
  }
  const me = (() => {
    try { return JSON.parse(sessionStorage.getItem("mysilo_user") || "null"); } catch { return null; }
  })();
  const labelFor = (uid) => {
    const m = teamMembers.find((t) => t.id === uid);
    if (!m) return "Someone";
    if (me && m.id === me.id) return "Me";
    return m.name || m.email;
  };

  return (
    <div className="space-y-8" data-testid="staging-tab">
      {/* Feb 22, 2026 (Sprint Request 6) — Ask Me Anything at the TOP of
          the Staging tab (above the Capture Pad), per Randy's spec. Same
          component as the email-kid / family-kid views so it inherits any
          future upgrades automatically. */}
      {siloSlug && (
        <FamilyAsk siloSlug={siloSlug} siloId={siloId} defaultOpen={true} />
      )}
      {/* ============ Section 1: Header ============ */}
      <div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="font-serif-display text-3xl text-[#1C1B1A]" data-testid="staging-title">
            Impromptu staging
          </h2>
          <button onClick={() => { setHelpView("welcome"); setShowInstallHelp(true); }}
            data-testid="staging-welcome-toggle"
            title="What is Staging? + home-screen setup" aria-label="Open the Staging welcome"
            className="p-1 rounded-full hover:bg-[#F0F1EE] text-[#4A5D4E]">
            <Eye size={18} weight="duotone" />
          </button>
        </div>
        <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl leading-relaxed">
          A pocket capture pad for any silo. Dictate a thought, push it into the silo, and (optionally)
          set a recurring push notification that surfaces a question from this silo at the time you choose.
        </p>
      </div>

      {/* Feb 2026 pt89g (Randy) — PWA "Heads up" banner removed. The same
          info lives in the eye tooltip next to the "Impromptu staging"
          title, so this was a duplicate nag. */}

      {showInstallHelp && (
        <StagingWelcomeModal
          siloSlug={siloSlug}
          initialView={helpView}
          onClose={() => setShowInstallHelp(false)}
        />
      )}

      {/* ============ Section 2: Staging area ============ */}
      <section className="bg-white border hairline rounded-lg p-5" data-testid="staging-area">
        <div className="flex items-center gap-2 mb-3">
          <Lightning size={16} weight="duotone" className="text-[#C16E5A]" />
          <h3 className="font-serif-display text-lg text-[#1C1B1A]">Capture pad</h3>
          {/* Feb 2026 (Randy) — Eye tooltip explaining the dual role of the
              Capture pad: calendar dictation + free-form Silo-bound stories
              & notes. Replaces the microcopy that used to live on the right. */}
          <InfoTip icon="eye" iconSize={13} placement="bottom-right" width={340} mobileCenter
            testid="capture-pad-eye" label="What else can I do with the Capture pad?">
            This isn&apos;t just an amazing way to dictate an event into your family calendar. Its also your way to capture ANY impromptu story, or note and send it directly into your Silo. You keep meaning to remember the clothing sizes for your partner. Want to tell a story about how well your camping trip was, send it to the Silo, you care about, that can relay it to those you want to see it.
          </InfoTip>
          <span className="flex-1" />
          <button
            onClick={() => setStagingText("")}
            disabled={!stagingText}
            data-testid="staging-clear"
            title="Clear the capture pad"
            className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border hairline text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] disabled:opacity-40 disabled:hover:text-[#6B6A68] disabled:hover:border-black/10">
            Clear
          </button>
        </div>
        <textarea
          ref={stagingRef}
          value={stagingText}
          onChange={(e) => setStagingText(e.target.value)}
          placeholder="Type or tap Record below. Multiple recordings will stack."
          rows={8}
          data-testid="staging-textarea"
          className="w-full px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E] resize-y"
        />
        {/* Jun 2026 — live words-as-you-talk preview (browser engine). The
            final saved text still comes from Whisper after you stop. */}
        {recording && (
          <div className="mt-2 px-3 py-2 rounded-md bg-[#FBF1EE] border border-[#E8D5CF] text-xs text-[#6B6A68] italic flex items-start gap-2" data-testid="staging-live-transcript">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse mt-1 shrink-0" />
            <span>{liveTranscript || "Listening… speak now."}</span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {!recording ? (
            <button onClick={startRecording} disabled={transcribing} data-testid="staging-rec-start"
              className="text-xs px-3 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#A55A48] flex items-center gap-1.5 disabled:opacity-50">
              <Microphone size={14} weight="duotone" /> Record
            </button>
          ) : (
            <button onClick={stopRecording} data-testid="staging-rec-stop"
              className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5">
              <Stop size={14} weight="fill" /> Stop · {String(Math.floor(recElapsed / 60)).padStart(2, "0")}:{String(recElapsed % 60).padStart(2, "0")}
            </button>
          )}
          {transcribing && <span className="text-xs text-[#6B6A68] flex items-center gap-1"><Spinner size={12} className="animate-spin" /> Transcribing…</span>}
          <span className="flex-1" />
          {(owns("ai_calendar") || inheritedSkus.includes("ai_calendar")) && (
            <button onClick={smartCreate} disabled={!stagingText.trim() || smartBusy} data-testid="staging-smart-create"
              title="Let the AI detect events, tasks, routines and pings in this text (uses your key)"
              className="text-xs px-3 py-2 rounded-md border border-[#C19A4A] text-[#8A6A24] bg-[#F8F1E2] hover:bg-[#F3E7CB] flex items-center gap-1.5 disabled:opacity-50">
              {smartBusy ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="duotone" />} Smart create
              {smartProgress && smartProgress.total > 0 && (
                <span className="text-[10px] font-medium text-[#8A6A24]/80" data-testid="staging-smart-progress">
                  {smartProgress.done}/{smartProgress.total}
                </span>
              )}
            </button>
          )}
          <SmartEditUndoCenter siloId={siloId} />
          <button onClick={pushToSilo} disabled={!stagingText.trim()} data-testid="staging-push-btn"
            className="text-xs px-3 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
            <PaperPlaneRight size={14} weight="duotone" /> Push to silo
          </button>
        </div>
      </section>

      {/* ============ Section 2.5 moved: AI Calendar now sits BELOW the
          Question bank + Scheduled pings (Staging-only items) so those
          Staging features stay top-of-page even when the Calendar SKU is
          not owned. See the block further down for the actual render. */}

      {smartEditJobId && (
        <SmartEditPreviewModal
          siloId={siloId}
          jobId={smartEditJobId}
          onClose={() => setSmartEditJobId(null)}
          onDone={() => { window.dispatchEvent(new Event("mysilo:calendar-refresh")); }}
        />
      )}
      {smartItems && (
        <SmartCaptureModal
          siloId={siloId}
          items={smartItems}
          sourceText={stagingText}
          onAskQA={(q) => { window.location.href = `/s/${siloSlug}?auto_q=${encodeURIComponent((q || "").trim().slice(0, 600))}`; }}
          onSaveAsPing={(q) => { setSmartItems(null); setPingEditor({ kind: "ping", question: (q || "").trim().slice(0, 600), time: "09:00" }); }}
          onClose={() => setSmartItems(null)}
          onCommitted={() => setSmartItems(null)}
        />
      )}

      {pingEditor && (
        <EventEditorModal siloId={siloId} defaults={pingEditor}
          onClose={() => setPingEditor(null)}
          onSaved={() => { setPingEditor(null); window.dispatchEvent(new Event("mysilo:calendar-refresh")); toast.success("Ping saved — it now lives under Organizer pings."); }} />
      )}

      {/* ============ Section 3: Question bank & Scheduled pings (one frame — Jul 2026, Randy) ============ */}
      <section className="bg-white border hairline rounded-lg p-5" data-testid="staging-bank-pings">
        <div data-testid="staging-bank">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={16} weight="duotone" className="text-[#C19A4A]" />
          <h3 className="font-serif-display text-lg text-[#1C1B1A]">Question bank</h3>
          <button onClick={() => setShowPingInfo(true)} data-testid="ping-types-info-btn"
            title="Question bank + Scheduled pings vs Calendar Pings — what's the difference?"
            className="p-1 rounded-full text-[#6B6A68] hover:text-[#2C6E92] hover:bg-[#E3EFF7] transition-colors">
            <Info size={15} weight="duotone" />
          </button>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
            ({bank.length})
          </span>
        </div>
        <div className="flex gap-2 mb-3">
          <input value={newBankQ} onChange={(e) => setNewBankQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addBankQ(); }}
            placeholder="A question to surface in alerts (e.g., 'What's one kind thing I can do for my wife today?')"
            data-testid="staging-bank-input"
            className="flex-1 px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
          <button onClick={addBankQ} disabled={!newBankQ.trim()} data-testid="staging-bank-add"
            className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
            <Plus size={13} weight="bold" /> Add
          </button>
        </div>
        {bank.length === 0 ? (
          <div className="text-xs text-[#6B6A68] italic">Add questions you'd like your scheduled pings to randomly pick from.</div>
        ) : (
          <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
            {bank.map((b) => (
              <div key={b.id} className="flex items-start gap-2 text-sm bg-[#FDFCF8] border hairline rounded px-3 py-2" data-testid={`bank-q-${b.id}`}>
                {editingBankId === b.id ? (
                  <>
                    <input
                      autoFocus
                      value={editingBankText}
                      onChange={(e) => setEditingBankText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveEditBankQ(); if (e.key === "Escape") setEditingBankId(null); }}
                      data-testid={`bank-q-${b.id}-input`}
                      className="flex-1 text-sm px-2 py-1 border tpl-border rounded"
                    />
                    <button onClick={saveEditBankQ} className="text-[10px] uppercase tracking-[0.12em] text-[#4A5D4E] px-2" data-testid={`bank-q-${b.id}-save`}>Save</button>
                    <button onClick={() => setEditingBankId(null)} className="text-[10px] uppercase tracking-[0.12em] text-[#6B6A68] px-2">Cancel</button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-[#1C1B1A]">{b.question}</span>
                    <button onClick={() => startEditBankQ(b)} className="text-[#6B6A68] hover:text-[#1C1B1A]" title="Edit" data-testid={`bank-q-${b.id}-edit`}>
                      <PencilSimple size={13} />
                    </button>
                    <button onClick={() => delBankQ(b.id)} className="text-[#6B6A68] hover:text-[#C16E5A]" title="Remove">
                      <Trash size={13} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        </div>

        {/* ---- Scheduled pings: same frame — the bank above is the pool these pull from ---- */}
        <div className="mt-5 pt-4 border-t hairline" data-testid="staging-alerts">
        {bank.length > 0 && siloSlug && (
          <div className="bg-[#F0F1EE] border hairline rounded-md p-3 mb-4 text-[12px] text-[#1C1B1A]" data-testid="homescreen-hint">
            <strong>Tip:</strong> on your phone, first open this silo's <strong>public Q&amp;A page</strong> (the <em>/s/…</em> page your fans see), then tap
            Share → <em>Add to Home Screen</em> from that page. Pings only fire on a home-screen icon created from the public Q&amp;A — saving from any other tab won't work. Tapping that icon later opens the public Q&amp;A and auto-fires a random question from your bank.
          </div>
        )}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Bell size={16} weight="duotone" className="text-[#4A5D4E]" />
          <h3 className="font-serif-display text-lg text-[#1C1B1A]">Scheduled pings</h3>
          <span className="ml-auto flex items-center gap-2">
            {pushPerm !== "granted" ? (
              <button onClick={subscribePush} disabled={pushBusy} data-testid="enable-push-btn"
                className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
                <Bell size={12} /> {pushBusy ? "Enabling…" : "Enable notifications"}
              </button>
            ) : (
              <>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#2F6B3A] flex items-center gap-1">
                  <CheckCircle size={11} weight="duotone" /> push enabled
                </span>
                <button onClick={testPush} className="text-[10px] underline text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="test-push-btn">
                  send a test
                </button>
              </>
            )}
            <button onClick={() => setShowNewAlert(true)} data-testid="new-alert-btn"
              className="text-xs px-3 py-1.5 rounded-md border hairline text-[#1C1B1A] hover:border-[#4A5D4E] flex items-center gap-1.5">
              <Plus size={12} weight="bold" /> New ping
            </button>
          </span>
        </div>
        {alerts.length === 0 ? (
          <div className="text-xs text-[#6B6A68] italic">No pings yet. Add one above — set a time (or go full surprise mode), pick a source, done.</div>
        ) : (
          <div className="space-y-2">
            {alerts.map((a) => <AlertRow key={a.id} alert={a} onChange={loadAlerts} labelFor={labelFor} myId={me?.id} onEdit={() => setEditingAlert(a)} />)}
          </div>
        )}
        </div>
      </section>

      {showPingInfo && <PingTypesExplainer onClose={() => setShowPingInfo(false)} context="scheduled" />}

      {/* ============ Section 2.5 render: AI Calendar (moved BELOW Question
          bank + Scheduled pings per Feb 2026 restructure). This frame now
          houses Calendar Pings, Household Setup, Reward store and Family
          leaderboard as collapsible sub-buttons. ============ */}
      <CalendarSection siloId={siloId} siloName={siloName} siloSlug={siloSlug} inheritedSkus={inheritedSkus} />

      {(showNewAlert || editingAlert) && (
        <NewAlertModal
          siloId={siloId} siloName={siloName} bank={bank}
          teamMembers={teamMembers} myId={me?.id}
          editing={editingAlert}
          onClose={() => { setShowNewAlert(false); setEditingAlert(null); }}
          onCreated={() => { setShowNewAlert(false); setEditingAlert(null); loadAlerts(); }}
        />
      )}

      <DocSaveDialog
        open={commitState.open}
        onClose={() => setCommitState({ open: false, text: "", defaultTitle: "" })}
        content={commitState.text}
        sourceSiloId={siloId}
        sourceDoc={null}
        silos={allSilos.length ? allSilos : [{ id: siloId, name: siloName || "this silo" }]}
        defaultTitle={commitState.defaultTitle}
        initialTier={defaultPushTier}
        dialogTitle="Push to silo"
        checkOversize={(content, tier) => needsSplit(content, tier)}
        onOversize={({ title, tier, siloId: destId }) => {
          setSplitState({ open: true, content: commitState.text, siloId: destId, tier, title: title || "" });
          setCommitState({ open: false, text: "", defaultTitle: "" });
        }}
        onSaved={() => setStagingText("")}
      />

      <SplitAndPushModal
        open={splitState.open}
        content={splitState.content}
        defaultTitle={splitState.title}
        siloId={splitState.siloId}
        siloName={allSilos.find((s) => s.id === splitState.siloId)?.name || siloName}
        initialTier={splitState.tier}
        onClose={() => setSplitState((s) => ({ ...s, open: false }))}
        onComplete={() => { setStagingText(""); setSplitState((s) => ({ ...s, open: false })); }}
      />
    </div>
  );
}

// =================== Helpers + sub-components ===================

function StagingWelcomeModal({ siloSlug, initialView = "welcome", onClose }) {
  const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const [view, setView] = useState(initialView === "howto" ? "howto" : "welcome");
  // Feb 24, 2026 (Randy pt89c-xxi) — "Don't ask me again" kill-switch.
  const [neverAsk, setNeverAsk] = useState(() => {
    try { return localStorage.getItem("staging_welcome_never_ask") === "1"; }
    catch { return false; }
  });
  const toggleNeverAsk = () => {
    const next = !neverAsk;
    setNeverAsk(next);
    try { localStorage.setItem("staging_welcome_never_ask", next ? "1" : "0"); } catch { /* */ }
  };
  const goPublicQA = () => { if (siloSlug) window.location.href = `/s/${siloSlug}`; };
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="staging-welcome-modal">
      <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-md shadow-2xl border hairline max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {view === "welcome" ? (
          <>
            <div className="px-5 py-3 border-b hairline flex items-center justify-between">
              <div className="flex items-center gap-2"><Lightning size={18} weight="duotone" className="text-[#C16E5A]" /><h2 className="font-serif-display text-lg">Welcome to Staging</h2></div>
              <button onClick={onClose} data-testid="welcome-modal-close" className="p-1 rounded hover:bg-[#F0F1EE]"><X size={18} /></button>
            </div>
            <div className="p-5 text-[13px] leading-relaxed space-y-3 text-[#1C1B1A]">
              <p>Staging is your silo's quiet back room — a fast, private place to feed and tune it.</p>
              <ul className="space-y-2">
                <li className="flex gap-2"><PaperPlaneRight size={15} weight="duotone" className="text-[#4A5D4E] mt-0.5 shrink-0" /><span><strong>Drop thoughts in fast.</strong> Type or dictate a note and push it straight into your silo — a quick pasteboard for ideas the moment they hit.</span></li>
                <li className="flex gap-2"><Bell size={15} weight="duotone" className="text-[#4A5D4E] mt-0.5 shrink-0" /><span><strong>Set up notifications.</strong> Make your public Q&amp;A notification-aware and schedule "pings" — questions that nudge you (or your fans) at the times you choose.</span></li>
              </ul>
              <div className="bg-[#F8F4E8] border border-[#E5C77F] rounded-md p-2.5 text-[12px] text-[#7A5200]" data-testid="welcome-difference-note">
                <strong>One thing about pings:</strong> they only reach a home-screen icon you save <strong>from your silo's public Q&amp;A page</strong> — not one added from your dashboard. The icon has to come from the same public Q&amp;A where you build the pings.
              </div>
              <button onClick={() => setView("howto")} data-testid="welcome-howto-link"
                className="text-[13px] font-medium text-[#4A5D4E] underline hover:text-[#1C1B1A]">
                How do I add it to my home screen? →
              </button>
            </div>
            <div className="px-5 py-3 border-t hairline flex items-center justify-between gap-3">
              <label className="flex items-center gap-1.5 text-[11px] text-[#6B6A68] cursor-pointer" data-testid="staging-welcome-never-ask">
                <input type="checkbox" checked={neverAsk} onChange={toggleNeverAsk} className="h-3.5 w-3.5" />
                Don&apos;t show this again
              </label>
              <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]">Close</button>
            </div>
          </>
        ) : (
          <>
            <div className="px-5 py-3 border-b hairline flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button onClick={() => setView("welcome")} data-testid="howto-back" aria-label="Back" className="p-1 rounded hover:bg-[#F0F1EE] text-[#4A5D4E]"><CaretLeft size={16} weight="bold" /></button>
                <House size={18} weight="duotone" className="text-[#4A5D4E]" /><h2 className="font-serif-display text-lg">Add to your home screen</h2>
              </div>
              <button onClick={onClose} data-testid="install-modal-close" className="p-1 rounded hover:bg-[#F0F1EE]"><X size={18} /></button>
            </div>
            <div className="p-5 text-[13px] leading-relaxed space-y-3 text-[#1C1B1A]">
              <div className="bg-[#F8F4E8] border border-[#E5C77F] rounded-md p-2.5 text-[12px] text-[#7A5200]" data-testid="install-public-qa-note">
                <strong>Important:</strong> do this from the silo's <strong>public Q&amp;A page</strong> (the <em>/s/…</em> page your fans see). A home-screen icon saved from any other screen won't deliver pings.
              </div>
              {isiOS ? (
                <>
                  <p>On iPhone or iPad:</p>
                  <ol className="list-decimal pl-5 space-y-1.5">
                    <li>In Safari, tap the <strong>Share</strong> icon (the square with the up-arrow at the bottom of the screen).</li>
                    <li>Scroll the share sheet and tap <strong>Add to Home Screen</strong>.</li>
                    <li>Tap <strong>Add</strong> in the top right.</li>
                    <li>Open My Silo from the new icon on your home screen — that's the version that can receive pings.</li>
                  </ol>
                  <p className="text-[11px] text-[#6B6A68] italic">iOS 16.4 or later is required for push notifications. If you don't see the "Add to Home Screen" option, you may need to use Safari (not Chrome or another browser).</p>
                </>
              ) : (
                <>
                  <p>On your phone:</p>
                  <ol className="list-decimal pl-5 space-y-1.5">
                    <li>Open My Silo in your phone's browser.</li>
                    <li>Tap the browser menu (⋮ on Android Chrome).</li>
                    <li>Tap <strong>Add to Home screen</strong> or <strong>Install app</strong>.</li>
                    <li>Open My Silo from the icon — that version can receive pings.</li>
                  </ol>
                </>
              )}
            </div>
            <div className="px-5 py-3 border-t hairline flex items-center justify-between gap-3">
              <label className="flex items-center gap-1.5 text-[11px] text-[#6B6A68] cursor-pointer" data-testid="staging-welcome-never-ask-howto">
                <input type="checkbox" checked={neverAsk} onChange={toggleNeverAsk} className="h-3.5 w-3.5" />
                Don&apos;t show this again
              </label>
              <button onClick={goPublicQA} disabled={!siloSlug} data-testid="welcome-go-public-qa"
                className="text-xs px-4 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
                Take me to my public Q&amp;A <ArrowSquareOut size={14} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Jul 2026 (Randy) — the "what's the difference?" explainer was extracted to
// its own file (components/PingTypesExplainer.jsx) so it can be opened from
// both the Question bank header AND the Calendar Pings header (which now
// lives inside the AI Calendar frame). Import it up top.

function NewAlertModal({ siloId, siloName, bank, teamMembers = [], myId, editing = null, onClose, onCreated }) {
  // May 25, 2026 — when `editing` is set, seed all the fields from the
  // alert and route submit through PATCH /staging/alerts/<id> instead of
  // POST /staging/alerts. Same modal, dual-purpose.
  const seedHour12 = () => {
    if (!editing) return 7;
    const h = editing.hour % 12;
    return h === 0 ? 12 : h;
  };
  const seedAmpm = () => editing ? (editing.hour >= 12 ? "PM" : "AM") : "AM";
  const [name, setName] = useState(editing?.name || `Daily ping for ${siloName}`);
  const [hour, setHour] = useState(seedHour12);
  const [minute, setMinute] = useState(editing?.minute ?? 0);
  const [ampm, setAmpm] = useState(seedAmpm);
  const [dow, setDow] = useState(editing?.dow || []);  // empty = daily
  const [sourceKind, setSourceKind] = useState(editing?.source_kind || (bank.length > 0 ? "random_bank" : "specific"));
  const [specific, setSpecific] = useState(editing?.specific_question || "");
  const [broadcast, setBroadcast] = useState(!!editing?.broadcast_to_subscribers);
  const [busy, setBusy] = useState(false);
  // Jul 2026 (Randy) — surprise mode: 1-3 pings at random times each day,
  // re-rolled daily, never inside the no-go window.
  const [scheduleMode, setScheduleMode] = useState(editing?.schedule_mode || "fixed");
  const [randomPerDay, setRandomPerDay] = useState(editing?.random_per_day || 1);
  const [quietStart, setQuietStart] = useState(editing?.quiet_start || "23:00");
  const [quietEnd, setQuietEnd] = useState(editing?.quiet_end || "07:00");
  // Feb 23, 2026 — collaboration: who gets the ping? Default = me.
  const [targetUserId, setTargetUserId] = useState(editing?.target_user_id || myId || "");
  const hasTeam = teamMembers.length > 1;

  const toggleDow = (d) => setDow((cur) => cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]);

  const submit = async () => {
    setBusy(true);
    try {
      // Convert 12-hour → 24-hour
      let h24 = hour % 12 + (ampm === "PM" ? 12 : 0);
      const canBroadcast = sourceKind === "random_bank" || sourceKind === "specific";
      const body = {
        name, hour: h24, minute: Number(minute),
        dow, source_kind: sourceKind,
        specific_question: sourceKind === "specific" ? specific : null,
        tz_offset_minutes: new Date().getTimezoneOffset(),
        target_user_id: targetUserId || myId || undefined,
        broadcast_to_subscribers: canBroadcast ? broadcast : false,
        schedule_mode: scheduleMode,
        random_per_day: Number(randomPerDay),
        quiet_start: scheduleMode === "random" ? (quietStart || null) : null,
        quiet_end: scheduleMode === "random" ? (quietEnd || null) : null,
      };
      if (editing) {
        await api.patch(`/staging/alerts/${editing.id}`, body);
        toast.success("Ping updated.");
      } else {
        await api.post("/staging/alerts", { silo_id: siloId, enabled: true, ...body });
        toast.success("Ping scheduled.");
      }
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || (editing ? "Could not save changes." : "Could not create ping."));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="new-alert-modal">
      <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-md shadow-2xl border hairline max-h-[90dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b hairline flex items-center justify-between shrink-0">
          <h2 className="font-serif-display text-lg flex items-center gap-2"><Bell size={16} weight="duotone" /> {editing ? "Edit scheduled ping" : "New scheduled ping"}</h2>
          <button onClick={onClose} data-testid="alert-modal-close" className="p-1 rounded hover:bg-[#F0F1EE]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm flex-1 overflow-y-auto">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="alert-name"
              className="mt-1 w-full px-3 py-2 border hairline rounded-md text-sm bg-white focus:outline-none focus:border-[#4A5D4E]" />
          </label>
          <div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">When</span>
            <div className="flex rounded-md border hairline overflow-hidden mt-1">
              <button type="button" onClick={() => setScheduleMode("fixed")} data-testid="alert-mode-fixed"
                className={`flex-1 text-xs px-2 py-2 flex items-center justify-center gap-1.5 ${scheduleMode === "fixed" ? "bg-[#1C1B1A] text-white" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}>
                <Clock size={12} weight="duotone" /> Set time
              </button>
              <button type="button" onClick={() => setScheduleMode("random")} data-testid="alert-mode-random"
                className={`flex-1 text-xs px-2 py-2 flex items-center justify-center gap-1.5 ${scheduleMode === "random" ? "bg-[#4A5D4E] text-white" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}>
                <Shuffle size={12} weight="duotone" /> Surprise me
              </button>
            </div>
          </div>
          {scheduleMode === "fixed" ? (
          <div className="flex items-end gap-2">
            <label className="flex-1">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Hour</span>
              <select value={hour} onChange={(e) => setHour(Number(e.target.value))} data-testid="alert-hour"
                className="mt-1 w-full px-2 py-2 border hairline rounded-md text-sm bg-white">
                {HOURS_12.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </label>
            <label className="flex-1">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Min</span>
              <select value={String(minute).padStart(2, "0")} onChange={(e) => setMinute(Number(e.target.value))} data-testid="alert-minute"
                className="mt-1 w-full px-2 py-2 border hairline rounded-md text-sm bg-white">
                {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="flex-1">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">AM/PM</span>
              <select value={ampm} onChange={(e) => setAmpm(e.target.value)} data-testid="alert-ampm"
                className="mt-1 w-full px-2 py-2 border hairline rounded-md text-sm bg-white">
                <option value="AM">AM</option><option value="PM">PM</option>
              </select>
            </label>
          </div>
          ) : (
          <div className="border border-[#B9D4BE] rounded-md p-3 bg-[#F4F8F5] space-y-2.5" data-testid="alert-random-controls">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Surprises per day</span>
              <div className="flex rounded-md border hairline overflow-hidden mt-1 bg-white">
                {[1, 2, 3].map((n) => (
                  <button key={n} type="button" onClick={() => setRandomPerDay(n)} data-testid={`alert-random-count-${n}`}
                    className={`flex-1 text-sm px-2 py-2 ${Number(randomPerDay) === n ? "bg-[#4A5D4E] text-white" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}>
                    {n}×
                  </button>
                ))}
              </div>
            </label>
            <div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">No-go window (never ping between)</span>
              <div className="flex items-center gap-2 mt-1">
                <input type="time" value={quietStart} onChange={(e) => setQuietStart(e.target.value)} data-testid="alert-quiet-start"
                  className="flex-1 px-2 py-2 border hairline rounded-md text-sm bg-white" />
                <span className="text-[10px] text-[#6B6A68]">and</span>
                <input type="time" value={quietEnd} onChange={(e) => setQuietEnd(e.target.value)} data-testid="alert-quiet-end"
                  className="flex-1 px-2 py-2 border hairline rounded-md text-sm bg-white" />
              </div>
            </div>
            <p className="text-[10px] text-[#4A5D4E] leading-relaxed">
              Times are re-rolled <strong>fresh every day</strong> — {randomPerDay}× at genuinely random
              moments, spread across the day, never inside the window above. You'll never see it coming.
            </p>
          </div>
          )}
          <div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Days</span>
            <div className="flex gap-1 mt-1">
              {DOW_LABELS.map((lbl, i) => (
                <button key={i} onClick={() => toggleDow(i)} type="button" data-testid={`alert-dow-${i}`}
                  className={`flex-1 py-1.5 text-xs rounded ${dow.includes(i) ? "bg-[#4A5D4E] text-white" : "bg-white border hairline text-[#6B6A68]"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[#6B6A68] mt-1 italic">{dow.length === 0 ? "Every day" : `${dow.length} day${dow.length === 1 ? "" : "s"} per week`}</div>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Question source</span>
            <select value={sourceKind} onChange={(e) => setSourceKind(e.target.value)} data-testid="alert-source"
              className="mt-1 w-full px-2 py-2 border hairline rounded-md text-sm bg-white">
              <option value="random_bank">{`Random from my question bank (${bank.length})`}</option>
              <option value="specific">A specific question (type it below)</option>
              <option value="random_history">Random from past chats on this silo</option>
              <option value="random_either">Random from EITHER bank or chat history</option>
            </select>
          </label>
          {sourceKind === "specific" && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">The question</span>
              <input value={specific} onChange={(e) => setSpecific(e.target.value)} data-testid="alert-specific"
                placeholder="e.g., What's one kind thing I can do for my wife today?"
                className="mt-1 w-full px-3 py-2 border hairline rounded-md text-sm bg-white focus:outline-none focus:border-[#4A5D4E]" />
            </label>
          )}
          {/* Jun 2026 — broadcast this ping to subscribers who opted in. */}
          <label className={`flex items-start gap-2 ${(sourceKind === "random_bank" || sourceKind === "specific") ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}>
            <input
              type="checkbox"
              checked={broadcast && (sourceKind === "random_bank" || sourceKind === "specific")}
              disabled={!(sourceKind === "random_bank" || sourceKind === "specific")}
              onChange={(e) => setBroadcast(e.target.checked)}
              data-testid="alert-broadcast-subscribers"
              className="mt-0.5"
            />
            <span className="text-[11px] text-[#1C1B1A] leading-relaxed">
              <strong>Also send to subscribers</strong> who turned on notifications for this silo.
              <span className="block text-[#6B6A68] mt-0.5">
                Only a bank question or a specific question can broadcast (this keeps your private chats private). Subscribers opt in from their own fan dashboard.
              </span>
            </span>
          </label>
          {/* Feb 23, 2026 — collaboration: pick whose phone gets the ping.
              Only shown when this silo has >1 person on it. */}
          {hasTeam && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Send to</span>
              <select value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)}
                data-testid="alert-target-user"
                className="mt-1 w-full px-2 py-2 border hairline rounded-md text-sm bg-white">
                {teamMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.id === myId ? "Me" : (m.name || m.email)}{m.role === "owner" ? " (owner)" : ""}
                  </option>
                ))}
              </select>
              <span className="text-[10px] text-[#6B6A68] italic mt-1 block">
                This ping will pop only on the chosen person's phone.
              </span>
            </label>
          )}
        </div>
        <div className="px-5 py-3 border-t hairline flex justify-end gap-2 shrink-0 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]">
          <button onClick={onClose} className="text-xs px-3 py-2 rounded text-[#6B6A68]">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="alert-create-submit"
            className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50">
            {busy ? "Saving…" : editing ? "Save changes" : "Create ping"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AlertRow({ alert, onChange, labelFor, myId, onEdit }) {
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  // Feb 23, 2026 — collaboration: only the alert's creator can toggle /
  // delete / preview / edit it. If I'm just the recipient, controls are
  // read-only and we show a "→ targeted at me by <creator>" label.
  const isCreator = !myId || alert.user_id === myId;
  const isJustRecipient = !isCreator && alert.target_user_id === myId;
  const fmt12 = () => {
    if (alert.schedule_mode === "random") {
      const n = alert.random_per_day || 1;
      const win = alert.quiet_start && alert.quiet_end ? ` (not ${alert.quiet_start}–${alert.quiet_end})` : "";
      return `🎲 ${n}× a day at random${win}`;
    }
    const h = alert.hour % 12 === 0 ? 12 : alert.hour % 12;
    const ampm = alert.hour >= 12 ? "PM" : "AM";
    return `${h}:${String(alert.minute).padStart(2, "0")} ${ampm}`;
  };
  const dowLabel = !alert.dow || alert.dow.length === 0
    ? "every day"
    : alert.dow.map((d) => DOW_LABELS[d]).join("·");
  const sourceLabel = ({
    random_bank: "random from bank",
    specific: alert.specific_question ? `"${alert.specific_question.slice(0, 50)}${alert.specific_question.length > 50 ? "…" : ""}"` : "specific",
    random_history: "random from chats",
    random_either: "random from either",
  })[alert.source_kind] || alert.source_kind;
  // Feb 2026 (Randy) — recipient tag is ALWAYS visible now (was only shown
  // when target != creator, which meant solo owners never saw where their
  // 7am ping was going). Now every scheduled ping row makes it obvious
  // exactly which device buzzes.
  let targetTag = null;
  if (labelFor) {
    const tgt = alert.target_user_id || alert.user_id;
    if (isCreator) {
      targetTag = `→ ${tgt === myId ? "just me" : labelFor(tgt) || "just me"}`;
    } else if (isJustRecipient) {
      targetTag = `from ${labelFor(alert.user_id) || "family"}`;
    } else {
      targetTag = `→ ${labelFor(tgt) || "family"}`;
    }
  }
  const toggle = async () => {
    setBusy(true);
    try {
      await api.patch(`/staging/alerts/${alert.id}`, { enabled: !alert.enabled });
      onChange();
    } catch { toast.error("Toggle failed."); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this ping?")) return;
    setBusy(true);
    try {
      await api.delete(`/staging/alerts/${alert.id}`);
      onChange();
    } catch { toast.error("Delete failed."); } finally { setBusy(false); }
  };
  const preview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post(`/staging/alerts/${alert.id}/preview`);
      const q = r.data?.question || "";
      if (r.data?.sent > 0) {
        toast.success(`Sent to ${r.data.sent} device${r.data.sent === 1 ? "" : "s"} — check the lock screen.`, { id: `preview-ok-${alert.id}` });
      } else {
        toast.error("No devices received the push. Try re-enabling notifications.", { id: `preview-none-${alert.id}` });
      }
      // May 25, 2026 — removed the secondary "Question used: ..." toast.
      // The success/failure toast above is enough on its own; the
      // duplicate toast was visually noisy and obscured the underlying
      // error toast when delivery failed.
      void q;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed.", { id: `preview-fail-${alert.id}` });
    } finally { setPreviewing(false); }
  };
  return (
    <div className="flex items-center gap-3 bg-[#FDFCF8] border hairline rounded px-3 py-2 text-sm" data-testid={`alert-row-${alert.id}`}>
      <div className="flex-1 min-w-0">
        <div className={`text-[#1C1B1A] ${!alert.enabled ? "opacity-50 line-through" : ""}`}>
          <strong>{alert.name}</strong>
          {targetTag && (
            <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E] bg-[#F0F1EE] px-1.5 py-0.5 rounded">
              {targetTag}
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#6B6A68] mt-0.5">
          {fmt12()} · {dowLabel} · {sourceLabel}
          {isJustRecipient && <span className="italic"> · read-only</span>}
        </div>
      </div>
      {isCreator && (
        <>
          <button onClick={preview} disabled={previewing || busy} data-testid={`alert-preview-${alert.id}`}
            title="Send this ping right now"
            className="text-[#6B6A68] hover:text-[#4A5D4E] disabled:opacity-40">
            <PaperPlaneRight size={14} weight="duotone" />
          </button>
          <button onClick={onEdit} disabled={busy} data-testid={`alert-edit-${alert.id}`}
            title="Edit time, day, or question"
            className="text-[#6B6A68] hover:text-[#1C1B1A] disabled:opacity-40">
            <PencilSimple size={14} weight="duotone" />
          </button>
          <button onClick={toggle} disabled={busy} data-testid={`alert-toggle-${alert.id}`} title={alert.enabled ? "Pause" : "Resume"}
            className="text-[#6B6A68] hover:text-[#1C1B1A]">
            {alert.enabled ? <Pause size={14} weight="duotone" /> : <Play size={14} weight="duotone" />}
          </button>
          <button onClick={remove} disabled={busy} data-testid={`alert-delete-${alert.id}`}
            className="text-[#6B6A68] hover:text-[#C16E5A]"><Trash size={14} /></button>
        </>
      )}
    </div>
  );
}

// utils
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
}
